import { useMemo, useState } from "react";
import { bool, object, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { BreakdownTableDepths } from "@repo/utils";
import { generateBufferId } from "@repo/structure";
import { useFeatureGuardian } from "@hooks";
import { LOCKED_FEATURES } from "@utils";
import { useEstimateStructure } from "@tools";
import { DEFAULT_PERCENT } from "@tools/EstimateEditor/utils";
import { Button, Dialog, InfoLabel, MenuButton, SortableListV2, Tag, TooltipIcon } from "@components";
import Grid from "@material-ui/core/Grid";
import { Badge } from "@material-ui/core";
import { AddBufferButton, BufferEntry, NewBuffer } from "./components";
import { Stacks } from "@assets";
import classnames from "classnames";
import { makeStyles } from "@material-ui/core/styles";

export const ItemBuffers = observer(({
  element,
  elementExpanded,
  lighterColor,
  useMenuButton,
}) => {
  const structure = useEstimateStructure();
  const { t } = useTranslation();
  const classes = useStyle();
  
  const [ visible, show ] = useState(false);
  const [ tmpBuffer, setTmpBuffer ] = useState(null);
  
  const { checkAccess, FeatureAlert } = useFeatureGuardian(
    LOCKED_FEATURES.BUFFERS
  );
  
  const projectBuffers = structure.settings.buffers || [];
  const parentBuffers = element.parentBuffers || [];
  const displayBuffers = element.buffers || [];
  const availableWorkTypes = element.taskWorkTypes;
  const usedChildBuffers = element.childUsedBuffers.length;
  const usedElementBuffers = displayBuffers.filter((b) => b.used).length;
  const usedProjectBuffers = projectBuffers.filter((b) => b.used).length;
  
  const activeBufferBadge = useMemo(() => (
    usedElementBuffers > 0 || (element.lvl === BreakdownTableDepths.SECTION && usedProjectBuffers > 0)
  ), [usedElementBuffers, usedProjectBuffers]);
  
  const forceButtonDisplay = useMemo(() => (
    activeBufferBadge || (usedChildBuffers > 0 && !elementExpanded)
  ), [ activeBufferBadge, usedChildBuffers, elementExpanded ]);
  
  const usedBuffers = useMemo(() => {
    let b = usedElementBuffers;
    
    if(element.lvl === BreakdownTableDepths.SECTION)
      b += usedProjectBuffers;
    if(!elementExpanded)
      b += usedChildBuffers;
    
    return b;
  }, [usedElementBuffers, usedProjectBuffers, usedChildBuffers, elementExpanded]);
  
  const dialogTitle = useMemo(() => (
    t("views.editor.dialogs.buffers.title.item")
  ), []);
  
  const handleOpen = async () => {
    const allowed = await checkAccess();
    if (!allowed) return;
    show(true);
  }
  
  const handleClose = () => {
    show(false);
    setTmpBuffer(null);
  }
  
  const addNewBuffer = () => {
    element.addBuffer(tmpBuffer);
    setTmpBuffer(null);
  }
  
  const addTmpBuffer = async () => {
    const name = t("views.editor.dialogs.buffers.name_new");
    const oldNameUses = displayBuffers.filter(ov => (
      ov.name.includes(name)
    )).length;
    
    setTmpBuffer({
      id: generateBufferId(),
      name: oldNameUses > 0 ? `${name} (${oldNameUses+1})` : name,
      percent: DEFAULT_PERCENT,
      workTypes: [],
      used: true,
    });
  }
  
  const handleProjectBuffersReorder = (list) => {
    structure.settings.reorderBuffers(list);
  };
  
  const handleDisplayBuffersReorder = (list) => {
    element.reorderBuffers(list);
  };
  
  const handleTmpBufferChange = (updatedData) => {
    setTmpBuffer({
      ...tmpBuffer,
      ...updatedData
    })
  }
  
  if(!useMenuButton && usedBuffers === 0)
    return <></>
  
  return (
    <>
      {
        useMenuButton &&
        <MenuButton
          icon={<Stacks color="primary" />}
          onClick={handleOpen}
        >
          {t("views.editor.dialogs.buffers.add")}
        </MenuButton>
      }
      {
        !useMenuButton &&
        <Badge
          badgeContent={forceButtonDisplay ? usedBuffers : 0}
          overlap="circular"
          color={ activeBufferBadge ? "primary" : "secondary" }
          aria-label="number of used buffers"
          max={9}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <TooltipIcon
            id="summarySettings"
            icon={<Stacks />}
            onClick={handleOpen}
            className={classnames("button-buffers noDrag", !forceButtonDisplay && "hoverIcon opq", lighterColor)}
            color="primary"
          >
            {dialogTitle}
          </TooltipIcon>
        </Badge>
      }
      {
        visible &&
        <Dialog
          open
          onClose={handleClose}
          width={624}
          title={<Grid container alignItems="center">
            <h2>{dialogTitle}</h2>
            <Tag text={element.name} className="ml-2 mt-1-5" />
          </Grid>}
          actions={
            <Button
              variant="contained"
              color="secondary"
              onClick={handleClose}
            >
              {t("common.close")}
            </Button>
          }
        >
          <Grid item container direction="column">
            {
              projectBuffers.length > 0 &&
              <InfoLabel
                label="views.editor.dialogs.buffers.subtitle.project"
                tooltipText="views.editor.dialogs.buffers.info.project"
                className="mb-1"
              />
            }
            {
              projectBuffers.length > 0 &&
              <SortableListV2
                list={projectBuffers}
                group="projectBuffers"
                onListReorder={handleProjectBuffersReorder}
                useDragHandle lockAxis
                itemContainerProps={{ alignItems: "flex-start" }}
                dragHandleProps={{ style: { marginTop: 14 } }}
                display="flex"
                direction="column"
              >
                {
                  projectBuffers.map((buffer) => (
                    <BufferEntry
                      key={buffer.id}
                      id={buffer.id}
                      buffer={buffer}
                      availableWorkTypes={availableWorkTypes}
                      readOnly
                    />
                  ))
                }
              </SortableListV2>
            }
            {/*{*/}
            {/*  projectBuffers.length > 0 &&*/}
            {/*  <Divider className="my-3" />*/}
            {/*}*/}
            <InfoLabel
              label="views.editor.dialogs.buffers.subtitle.item"
              tooltipText="views.editor.dialogs.buffers.info.item"
              className="mb-1 mt-6"
            />
            {
              parentBuffers.length > 0 &&
              <Grid
                item container
                direction="column"
                className={classes.parentBufferContainer}
              >
                {
                  parentBuffers.map((buffer) => (
                    <BufferEntry
                      key={buffer.id}
                      buffer={buffer}
                      availableWorkTypes={availableWorkTypes}
                      readOnly={buffer.lvl < element.lvl}
                    />
                  ))
                }
              </Grid>
            }
            {/*{*/}
            {/*  parentBuffers.length > 0 &&*/}
            {/*  <Divider className="my-2" />*/}
            {/*}*/}
            <SortableListV2
              list={displayBuffers}
              group="displayBuffers"
              onListReorder={handleDisplayBuffersReorder}
              useDragHandle lockAxis
              itemContainerProps={{ alignItems: "flex-start" }}
              dragHandleProps={{ style: { marginTop: 14 } }}
              display="flex"
              direction="column"
            >
              {
                displayBuffers.map((buffer) => (
                  <BufferEntry
                    key={buffer.id}
                    id={buffer.id}
                    buffer={buffer}
                    availableWorkTypes={availableWorkTypes}
                  />
                ))
              }
            </SortableListV2>
            {/*{*/}
            {/*  Boolean(displayBuffers.length) &&*/}
            {/*  <Divider className="my-3" />*/}
            {/*}*/}
            {
              tmpBuffer
                ? <NewBuffer
                  onChange={handleTmpBufferChange}
                  onAccept={addNewBuffer}
                  onCancel={() => setTmpBuffer(null)}
                  availableWorkTypes={availableWorkTypes}
                  {...tmpBuffer}
                />
                : <AddBufferButton onAdd={addTmpBuffer} itemBuffer />
            }
          </Grid>
        </Dialog>
      }
      <FeatureAlert />
    </>
  );
});

ItemBuffers.propTypes = {
  element: object.isRequired,
  lighterColor: string,
  elementExpanded: bool,
  useMenuButton: bool,
}

const useStyle = makeStyles((theme) => ({
  parentBufferContainer: {
    borderBottomWidth: 1,
    borderBottomStyle: "dashed",
    borderBottomColor: theme.props.border,
    paddingBottom: 8,
    marginBottom: 8,
  }
}));